import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircleHeart, X, SendHorizonal, Loader2, Volume2, VolumeX, Square, Mic, UploadCloud, Check, ClipboardList } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { API, api, absoluteUrl, formatApiError } from "../lib/api";
import { IMAGES, TREATMENTS } from "../content/siteContent";
import { gateSeason } from "./IntroGate";
import { NanoPanel } from "./NanoPanel";
import { toast } from "sonner";

const stripTokens = (text) => text.replace(/\[(PANELE|PORTAL)[_ -]?G[Iİ]T\]|\[DOKTOR[_ -]?KARTI\]|\[SAYFA_AC:[^\]\s]*\]|\[BASVURU_FORM:\{[^\]]*\}\]/gi, "").trim();
const stripTokensRaw = (text) => text.replace(/\[(PANELE|PORTAL)[_ -]?G[Iİ]T\]|\[DOKTOR[_ -]?KARTI\]|\[SAYFA_AC:[^\]\s]*\]|\[BASVURU_FORM:\{[^\]]*\}\]/gi, "");
const hasPanelToken = (text) => /\[(PANELE|PORTAL)[_ -]?G[Iİ]T\]/i.test(text);
const hasDoctorToken = (text) => /\[DOKTOR[_ -]?KARTI\]/i.test(text);
const getNavRoute = (text) => {
  const m = text.match(/\[SAYFA_AC:([^\]\s]+)\]/i);
  if (!m) return null;
  const r = m[1];
  return /^\/((tedavi|kanser)\/[\w-]+|panel|haberler|belgeler|giris)?$/.test(r) && r !== "" ? r : null;
};
// Tudi Live sahne haritası — konuşulan konuya/duruma göre arka plan sahnesi (kullanıcının yüklediği görseller)
const LIVE_SCENES = ["genel", "dinleme", "konusma", "beyin", "akciger", "kolon", "prostat", "pankreas", "lenf", "kemik", "kas", "meme", "karaciger", "mide", "yumurtalik"];
// İkinci varyantı olan sahneler (aynı konu bazen farklı açıdan gösterilir — site hep taze hissettirir)
const LIVE_VARIANTS = new Set(["beyin", "lenf", "kolon", "prostat", "pankreas", "kemik", "meme"]);
// Sahne açılırken Tudi'nin kısa SESLİ sunum cümlesi (sinema hissi)
const LIVE_PRESENT_LINES = {
  beyin: "Beyinle ilgili görüntüyü ekrana alıyorum.",
  akciger: "Akciğer görüntüsünü açıyorum.",
  kolon: "Kalın bağırsak görüntüsünü getiriyorum.",
  prostat: "Prostat bölgesini ekrana alıyorum.",
  pankreas: "Pankreası ekrana alıyorum.",
  lenf: "Lenfatik sistemi ve bağışıklık haritasını gösteriyorum.",
  kemik: "Kemik yapısını ekrana alıyorum.",
  kas: "Kas ve iskelet görüntüsünü getiriyorum.",
  meme: "Meme görüntülemesini açıyorum.",
  karaciger: "Karaciğeri ekrana alıyorum.",
  mide: "Mide görüntüsünü açıyorum.",
  yumurtalik: "Yumurtalık bölgesini ekrana alıyorum.",
};
const LIVE_TOPIC_KEYWORDS = [
  ["beyin", /beyin|glioblastom|gliom|menenjiom|hipofiz|nöro|noro/i],
  ["akciger", /akci[gğ]er|solunum|nefes|mezotelyoma/i],
  ["kolon", /kolon|kolorektal|ba[gğ][iı]rsak|rektum|rektal/i],
  ["prostat", /prostat|mesane|idrar kesesi|böbrek|bobrek/i],
  ["karaciger", /karaci[gğ]er|hepat|safra|siroz/i],
  ["pankreas", /pankreas|sarl[iı]k/i],
  ["lenf", /lenf|lösemi|losemi|hodgkin|kemik ili[gğ]i|kan kanseri|car[- ]?t|immünoterapi|immunoterapi|kanser a[şs][iı]s/i],
  ["kemik", /kemik|sarkom|osteosarkom|kal[cç]a|omurga|omurilik/i],
  ["kas", /kas[- ]iskelet|yumu[şs]ak doku|miyosarkom|liposarkom|rabdomiyosarkom|\bkas\b/i],
  ["meme", /meme|mammografi|memografi/i],
  ["mide", /mide|gastrik|midesi/i],
  ["yumurtalik", /yumurtal[iı]k|over\b|overian|uterus|rahim|jinekolojik/i],
];
const detectLiveTopic = (text) => {
  if (!text) return null;
  for (const [key, re] of LIVE_TOPIC_KEYWORDS) if (re.test(text)) return key;
  return null;
};

const getApplicationData = (text) => {
  const m = text.match(/\[BASVURU_FORM:(\{[^\]]*\})\]/i);
  if (!m) return null;
  try {
    const d = JSON.parse(m[1]);
    return d.name && d.email ? d : null;
  } catch { return null; }
};

// Whisper sessizlik/gürültü halüsinasyonları — bunlar kullanıcı mesaji DEGILDIR (Tudi'nin kendi kendine konuşmasını önler)
const STT_NOISE_RE = /^(you|thank\s?you|thanks|thank you for watching|bye|okay|ok|hmm+|hı|teşekkürler|teşekkür ederim|izlediğiniz için teşekkür(ler)?|görüşmek üzere|görüşürüz|hoşça ?kal|abone ol|altyazı|m\.?k\.?)[.!…\s]*$/i;
const isNoiseText = (s) => {
  const v = (s || "").trim().toLowerCase();
  if (v.length < 2) return true;
  if (/^[.!?,…\s-]+$/.test(v)) return true;
  return STT_NOISE_RE.test(v);
};

// Tudi'nin kendi TTS sesi mikrofona sızıp STT'den "kullanıcı mesajı" gibi dönerse yakala (yankı kalkanı)
const normSpeech = (s) => (s || "").toLowerCase().replace(/[^a-zçğıöşüâîû0-9]/gi, "");
const isEchoOf = (transcript, recent) => {
  const t = normSpeech(transcript);
  const r = normSpeech(recent);
  if (t.length < 6 || r.length < 6) return false;
  if (r.includes(t)) return true;
  const tail = r.slice(-120); // Tudi'nin son sözlerinin herhangi bir 25'lik parçası kopyada geçiyorsa: yankı
  for (let i = 0; i + 25 <= tail.length; i += 10) {
    if (t.includes(tail.slice(i, i + 25))) return true;
  }
  return false;
};

function DoctorCard() {
  const { t } = useLang();
  return (
    <div className="mt-2.5 rounded-2xl border border-brand/15 bg-white p-3.5 shadow-sm" data-testid="doctor-card">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-brand/10 flex items-center justify-center text-brand font-heading font-bold text-sm shrink-0">MA</div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-ink">Dr. Mehmet Akdemir</div>
          <div className="text-[10px] text-sub leading-tight">{t("doc_card_title")}</div>
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {[t("doc_chip1"), t("doc_chip2"), t("doc_chip3"), t("doc_chip4")].map((c) => (
          <span key={c} className="text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-mist text-sub">{c}</span>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <a href="tel:+905316702460" className="flex-1 h-8 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-branddeep transition-colors" data-testid="doctor-card-call">{t("doc_call")}</a>
        <a href="mailto:drmakdemir@gmail.com" className="flex-1 h-8 rounded-full bg-mist text-ink text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-black/10 transition-colors" data-testid="doctor-card-mail">{t("doc_mail")}</a>
      </div>
    </div>
  );
}

function ApplicationCard({ data }) {
  const { t } = useLang();
  const [kvkk, setKvkk] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const tr = TREATMENTS.find((x) => x.id === data.treatment);

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/applications", {
        name: data.name,
        email: data.email,
        treatment: data.treatment === "genel" ? "" : data.treatment,
        message: data.message || "",
        consent_kvkk: true,
        consent_agreement: true,
      });
      setDone(true);
      toast.success(t("form_success"));
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="mt-2.5 rounded-2xl border border-emerald-500/20 bg-emerald-50/60 p-3.5 text-xs font-semibold text-emerald-700 flex items-center gap-2" data-testid="apply-done">
        <Check size={14} /> {t("form_success")}
      </div>
    );
  }

  return (
    <div className="mt-2.5 rounded-2xl border border-brand/15 bg-white p-3.5 shadow-sm" data-testid="apply-card">
      <div className="text-xs font-heading font-bold text-ink flex items-center gap-1.5"><ClipboardList size={13} className="text-brand" /> {t("apply_title")}</div>
      <div className="mt-2 space-y-1 text-[11px] text-ink/80">
        <div><span className="text-sub">{t("apply_name")}:</span> <b>{data.name}</b></div>
        <div><span className="text-sub">E-posta:</span> <b>{data.email}</b></div>
        {tr && <div><span className="text-sub">{t("apply_treatment")}:</span> <b>{t(tr.nameKey)}</b></div>}
        {data.message && <div><span className="text-sub">{t("apply_summary")}:</span> {data.message}</div>}
      </div>
      <label className="mt-2.5 flex items-start gap-2 text-[10px] text-sub cursor-pointer">
        <input type="checkbox" checked={kvkk} onChange={(e) => setKvkk(e.target.checked)} className="mt-0.5 accent-brand" data-testid="apply-kvkk" />
        <span>{t("apply_kvkk")} (<a href="/belgeler" className="text-brand underline">/belgeler</a>)</span>
      </label>
      <button data-testid="apply-submit" onClick={submit} disabled={!kvkk || sending} className="mt-2.5 w-full h-9 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-branddeep disabled:opacity-50 transition-colors">
        {sending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {t("apply_submit")}
      </button>
    </div>
  );
}

export function ChatWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // Ziyaretçi konuşurken Tudi hâlâ cevap yazıyorsa sözü DÜŞÜRME — bitmesini bekle, sonra cevapla (busyRef aşağıda tanımlı)
  const waitForFree = async (maxMs = 25000) => {
    const t0 = Date.now();
    while (busyRef.current && Date.now() - t0 < maxMs) await new Promise((r) => setTimeout(r, 300));
    return !busyRef.current;
  };
  // Japon Kapisi'ndan girildiyse Tudi "nereden baslarsiniz?" tur selamiyla karsilar
  const [tourMode] = useState(() => {
    try {
      if (sessionStorage.getItem("tudi_tour_pending")) {
        sessionStorage.removeItem("tudi_tour_pending");
        return true;
      }
    } catch {}
    return false;
  });
  const sessionRef = useRef(localStorage.getItem("2d-chat-session") || (() => { const id = crypto.randomUUID(); localStorage.setItem("2d-chat-session", id); return id; })());
  const bottomRef = useRef(null);
  const greetedRef = useRef(false);
  const audioRef = useRef(null);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [ttsLoading, setTtsLoading] = useState(null);
  const [greetSpeaking, setGreetSpeaking] = useState(false);
  const [greeting, setGreeting] = useState(null);
  const [orbLine, setOrbLine] = useState("");
  const [orbSpeaking, setOrbSpeaking] = useState(false);
  const lastTtsTextRef = useRef(""); // Tudi'nin az önce SESLİ söylediği metin — yankı kalkanı bununla kıyaslar
  const [nanoOpen, setNanoOpen] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false); // tam ekran karşılıklı konuşma sahnesi (video tarzı)
  const [liveTopic, setLiveTopic] = useState(null); // konuşulan kanser konusu → sahne o organa döner
  const liveTopicRef = useRef(null);
  liveTopicRef.current = liveTopic;
  const liveOpenRef = useRef(false);
  liveOpenRef.current = liveOpen;
  const liveVideoRef = useRef(null); // açılış videosu
  const micBarRef = useRef(null);
  // Canlı sahnede mikrofon seviye çubuğu (kullanıcı sesinin duyulduğunu GÖRÜR)
  useEffect(() => {
    if (!liveOpen) return;
    let raf;
    const f = () => {
      if (micBarRef.current) {
        const lvl = window.__tudiMicLevel || 0;
        micBarRef.current.style.width = `${Math.round(lvl * 100)}%`;
      }
      raf = requestAnimationFrame(f);
    };
    raf = requestAnimationFrame(f);
    return () => cancelAnimationFrame(raf);
  }, [liveOpen]);

  // Dönen ziyaretçi için kişisel selamı önceden hazırla (ilk temasta sesli okunacak)
  useEffect(() => {
    api.post("/chat/greeting", { session_id: sessionRef.current })
      .then((r) => { if (r.data?.greeting) setGreeting(r.data.greeting); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const speak = async (i, text) => {
    if (speakingIdx === i) {
      audioRef.current?.pause();
      setSpeakingIdx(null);
      return;
    }
    try {
      setTtsLoading(i);
      speechGenRef.current += 1; // manuel okuma, kuyruk/selam zincirini keser
      ttsQueueRef.current = [];
      sentenceBufRef.current = "";
      ttsPlayingRef.current = false;
      setOrbSpeaking(false);
      greetSeqRef.current += 1;
      lastTtsTextRef.current = (lastTtsTextRef.current + " " + text).slice(-1200);
      cancelListeningRef.current?.(); // Tudi konuşurken mikrofon kaydı kendi sesini yakalamasın
      const r = await api.post("/tts", { text, lang });
      audioRef.current?.pause();
      const a = new Audio(absoluteUrl(r.data.url));
      audioRef.current = a;
      a.onended = () => { setSpeakingIdx(null); setTimeout(() => scheduleListenRef.current?.(), 400); };
      await a.play();
      setSpeakingIdx(i);
    } catch { setTimeout(() => scheduleListenRef.current?.(), 400); } finally {
      setTtsLoading(null);
    }
  };

  const stopAudio = () => {
    audioRef.current?.pause();
    setSpeakingIdx(null);
    setGreetSpeaking(false);
    greetSeqRef.current += 1; // devam eden selam zincirini de kes
    speechGenRef.current += 1; // TTS kuyruk döngüsünü de kes (bekleyen promise'lar çalmasın)
    ttsQueueRef.current = [];
    sentenceBufRef.current = "";
    setOrbSpeaking(false);
    window.dispatchEvent(new CustomEvent("tudi-say-end"));
  };

  // Tudi varsayılan olarak HER cevabı sesli söyler (kullanıcı kararı, 2026-09-20) — sessize alınabilir
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem("tudi_muted") === "1"; } catch { return false; } });
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const toggleMute = () => setMuted((m) => {
    const v = !m;
    try { localStorage.setItem("tudi_muted", v ? "1" : "0"); } catch {}
    if (v) stopAudio();
    return v;
  });

  // Tek ses kanalı garantisi: her yeni sesli niyet nesil sayacını artırır;
  // çalan/ bekleyen tüm zincirler (selam, kuyruk, nudge) nesli kontrol edip susar.
  const speechGenRef = useRef(0);
  const pendingSayRef = useRef(null); // otomatik oynatma engeline takılan konuşma — ilk dokunuşta çalar

  // --- anlik konusma: cevap akarken her tamamlanan cümle hemen seslendirilir (bekleme yok) ---
  const ttsQueueRef = useRef([]);
  const ttsPlayingRef = useRef(false);
  const sentenceBufRef = useRef("");
  const bargeRef = useRef(false);

  // --- ses-tepkili dalga halkası: çalan TTS sesi analiz edilip küre halkaları sesle nabız atar ---
  const waveCtxRef = useRef(null);
  const orbRingRef = useRef(null);
  const orbRing2Ref = useRef(null);
  const waveRafRef = useRef(null);

  const resetRings = () => {
    [orbRingRef, orbRing2Ref].forEach((r) => {
      if (r.current) { r.current.style.transform = "scale(1)"; r.current.style.opacity = "0"; }
    });
  };

  const attachWave = (a) => {
    try {
      if (!waveCtxRef.current) waveCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = waveCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const src = ctx.createMediaElementSource(a);
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      src.connect(an);
      an.connect(ctx.destination);
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        if (a.paused || a.ended) { resetRings(); return; }
        orbRingRef.current?.classList.remove("tudi-wave-live");
        orbRing2Ref.current?.classList.remove("tudi-wave-live-2");
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += Math.abs(v - 128);
        const level = Math.min(1, (sum / buf.length) / 6);
        if (orbRingRef.current) { orbRingRef.current.style.transform = `scale(${1 + level * 0.55})`; orbRingRef.current.style.opacity = String(0.25 + level * 0.6); }
        if (orbRing2Ref.current) { orbRing2Ref.current.style.transform = `scale(${1 + level * 0.95})`; orbRing2Ref.current.style.opacity = String(0.12 + level * 0.4); }
        window.__tudiLevel = level; // nano-panel pikselleri sesle nabız atar
        waveRafRef.current = requestAnimationFrame(tick);
      };
      a.addEventListener("play", () => { cancelAnimationFrame(waveRafRef.current); tick(); }, { once: true });
    } catch { /* dalga olmadan da konuşur */ }
  };

  const startBargeWatch = async () => {
    if (bargeRef.current || !handsFreeRef.current || !navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      bargeRef.current = true;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      let loudSince = null;
      const iv = setInterval(() => {
        if (!ttsPlayingRef.current) {
          clearInterval(iv);
          stream.getTracks().forEach((tr) => tr.stop());
          ctx.close().catch(() => {});
          bargeRef.current = false;
          return;
        }
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += Math.abs(v - 128);
        // Uyarlanabilir eşik: Tudi o anda yüksek sesle konuşuyorsa mikrofondaki gürültü büyük ihtimalle
        // Tudi'nin kendi sesinin yankısıdır — söz kesme eşiği TTS seviyesiyle birlikte yükselir.
        const ttsLvl = window.__tudiLevel || 0;
        const bargeThreshold = Math.max(4.5, ttsLvl * 10 + 1.5);
        if (sum / buf.length > bargeThreshold) {
          if (!loudSince) loudSince = Date.now();
          if (Date.now() - loudSince > 700) {
            clearInterval(iv);
            stream.getTracks().forEach((tr) => tr.stop());
            ctx.close().catch(() => {});
            bargeRef.current = false;
            ttsQueueRef.current = [];
            sentenceBufRef.current = "";
            stopAudio();
            setGreetSpeaking(false);
            setTimeout(() => scheduleListenRef.current?.(), 200);
          }
        } else {
          loudSince = null;
        }
      }, 150);
    } catch { /* izin yok */ }
  };

  const playTtsQueue = async () => {
    if (ttsPlayingRef.current) return;
    ttsPlayingRef.current = true;
    const gen = speechGenRef.current; // bu kuyruk çalışırken yeni bir sesli niyet başlarsa sus
    setOrbSpeaking(true);
    cancelListeningRef.current?.(); // süren dinleme kaydı Tudi'nin sesini yakalamasın diye iptal
    startBargeWatch();
    while (ttsQueueRef.current.length) {
      if (gen !== speechGenRef.current) break; // kesildi — bekleyen sesler çalınmaz
      const item = ttsQueueRef.current.shift();
      setOrbLine(item.text);
      lastTtsTextRef.current = (lastTtsTextRef.current + " " + item.text).slice(-1200);
      try {
        const url = await item.promise; // ses, önceki cümle çalarken çoktan hazırlanmış olur
        if (!url || gen !== speechGenRef.current) continue;
        if (audioRef.current && !audioRef.current.paused) audioRef.current.pause(); // tek kanal: önceki her ses susar
        const a = new Audio(url);
        attachWave(a);
        audioRef.current = a;
        const played = await new Promise((resolve) => { a.onended = () => resolve(true); a.onerror = () => resolve(true); a.play().then(() => {}).catch(() => resolve(false)); });
        if (!played) {
          // Tarayıcı otomatik oynatmayı engelledi (henüz kullanıcı etkileşimi yok) — kalan konuşma ilk dokunuşta çalar
          const rest = [item.text, ...ttsQueueRef.current.map((q) => q.text)].join(" ").trim();
          if (rest) pendingSayRef.current = rest;
          ttsQueueRef.current = [];
          break;
        }
      } catch { /* sonraki cümleye geç */ }
    }
    if (gen !== speechGenRef.current) { ttsPlayingRef.current = false; return; } // yeni zincir başladı; bayrakları ona bırak
    ttsPlayingRef.current = false;
    setOrbSpeaking(false);
    window.dispatchEvent(new CustomEvent("tudi-say-end")); // dergi "Tudi Anlatsın" butonu sıfırlansın
    if (handsFreeRef.current) setTimeout(() => scheduleListenRef.current?.(), 300);
  };

  const enqueueSpeech = (delta) => {
    sentenceBufRef.current += stripTokensRaw(delta);
    const parts = sentenceBufRef.current.match(/[^.!?\n]+[.!?]+/g);
    if (parts && parts.length) {
      const consumed = parts.join("").length;
      sentenceBufRef.current = sentenceBufRef.current.slice(consumed);
      parts.forEach((s) => {
        const t2 = s.replace(/\*\*/g, "").trim();
        if (t2.length > 2) ttsQueueRef.current.push({ text: t2, promise: api.post("/tts", { text: t2, lang }).then((r) => absoluteUrl(r.data.url)).catch(() => null) });
      });
      playTtsQueue();
    }
  };

  const flushSpeech = () => {
    const rest = stripTokens(sentenceBufRef.current).replace(/\*\*/g, "").trim();
    sentenceBufRef.current = "";
    if (rest.length > 2) ttsQueueRef.current.push({ text: rest, promise: api.post("/tts", { text: rest, lang }).then((r) => absoluteUrl(r.data.url)).catch(() => null) });
    playTtsQueue();
  };

  const sayText = (text) => {
    // Panel turu gibi doğrudan seslendirme: önce mevcut tüm sesleri sustur, sonra metni cümlelere bölüp kuyruğa al
    audioRef.current?.pause();
    greetSeqRef.current += 1;
    speechGenRef.current += 1;
    ttsQueueRef.current = [];
    sentenceBufRef.current = "";
    ttsPlayingRef.current = false;
    setGreetSpeaking(false);
    stripTokens(text).split(/(?<=[.!?])\s+/).forEach((s) => {
      const t2 = s.trim();
      if (t2.length > 2) ttsQueueRef.current.push({ text: t2, promise: api.post("/tts", { text: t2, lang }).then((r) => absoluteUrl(r.data.url)).catch(() => null) });
    });
    playTtsQueue();
  };

  // Sayfalar Tudi'ye doğrudan konuşturabilsin (örn. hasta paneli turu, dergi okuma)
  useEffect(() => {
    const handler = (e) => { if (e.detail?.text) sayText(e.detail.text); };
    const stopper = () => stopAudio();
    window.addEventListener("tudi-say", handler);
    window.addEventListener("tudi-stop", stopper);
    return () => { window.removeEventListener("tudi-say", handler); window.removeEventListener("tudi-stop", stopper); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Piksel panel YALNIZCA dokununca açılır — konuşma başlayınca ekrana ek katman çıkmaz (kullanıcı kararı, 2026-09-14)
  // Balon/yazı katmanları kaldırıldı: ekranda yalnızca Tudi küresi + WhatsApp durur

  // Selam sesini sayfa açılır açılmaz CÜMLE CÜMLE hazırla (ilk temasta anında başlar, akıcı devam eder)
  // Tur modunda: mevsimlik selam + "nereden başlarsınız" tek ses, tek akış
  const greetAudiosRef = useRef([]);
  const greetPreparedRef = useRef(""); // aynı selam metni için TTS yalnızca bir kez istenir
  useEffect(() => {
    const text = tourMode
      ? `${t(`intro_greeting_${gateSeason()}`)} ${t("tour_greeting")}`
      : (greeting || `${t("video_cap1a")}! ${t("chat_sub")}`);
    if (greetPreparedRef.current === text) return; // render başına değil, metin başına bir hazırlık
    greetPreparedRef.current = text;
    const sentences = stripTokens(text).split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter((s) => s.length > 2);
    let cancelled = false;
    Promise.all(
      sentences.map((s) => api.post("/tts", { text: s, lang }).then((r) => absoluteUrl(r.data.url)).catch(() => null))
    ).then((urls) => {
      if (cancelled) return;
      if (!urls.some(Boolean)) { greetPreparedRef.current = ""; return; } // tamamen başarısızsa tekrar denenebilir
      greetAudiosRef.current = urls
        .map((u, i) => (u ? { a: null, url: u, t: sentences[i] } : null))
        .filter(Boolean)
        .map((it) => {
          const a = new Audio(it.url);
          a.preload = "auto";
          return { a, t: it.t };
        });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [greeting, lang]);

  const greetSeqRef = useRef(0);
  const greetStartedRef = useRef(false); // selam bu oturumda başladı mı (tarayıcı engellerse sıfırlanıp ilk dokunuşta tekrar denenir)
  const playGreetingSeq = (idx = 0, token = null) => {
    if (token === null) {
      token = ++greetSeqRef.current;
      speechGenRef.current += 1; // selam başlıyorsa kuyruk/nudge zinciri susar
      ttsPlayingRef.current = false;
    }
    if (token !== greetSeqRef.current) return; // yeni bir konusma basladiysa bu zincir olur
    const items = greetAudiosRef.current;
    if (idx >= items.length) { setGreetSpeaking(false); resetRings(); return; }
    const { a, t: line } = items[idx];
    setOrbLine(line);
    lastTtsTextRef.current = (lastTtsTextRef.current + " " + line).slice(-1200);
    if (idx === 0) cancelListeningRef.current?.();
    // Piksel panel burada da otomatik AÇILMAZ — yalnızca kullanıcı küreye dokununca açılır
    const prev = audioRef.current;
    if (prev && prev !== a && !prev.paused) prev.pause(); // tek kanal: üst üste ses yok
    attachWave(a);
    audioRef.current = a; // stopAudio bunu da susturur
    a.onended = () => playGreetingSeq(idx + 1, token);
    a.play().then(() => { if (idx === 0) setGreetSpeaking(true); }).catch(() => {
      if (idx === 0) {
        greetStartedRef.current = false; // otomatik oynatma engellendi — ilk gerçek dokunuş tekrar deneyecek
      } else {
        playGreetingSeq(idx + 1, token);
      }
    });
  };

  // Her render'da güncellenir → global dinleyici daima taze closure çağırır
  const startGreetingRef = useRef(null);
  startGreetingRef.current = () => {
    if (greetStartedRef.current || mutedRef.current) return;
    if (!greetAudiosRef.current.length) return;
    greetStartedRef.current = true;
    playGreetingSeq(0);
  };

  // 1) Sesler hazır olunca otomatik dene (autoplay'e izinli tarayıcılarda anında başlar)
  useEffect(() => {
    if (!greetAudiosRef.current.length) return;
    const t = setTimeout(() => startGreetingRef.current?.(), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [greeting, lang]);

  // 2) GARANTİLİ YOL: ilk gerçek dokunuş/tuş — selam henüz başlamadıysa hemen başlat + ses motorunu ısıt (iOS/Android güvenli)
  useEffect(() => {
    const h = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx && !waveCtxRef.current) waveCtxRef.current = new Ctx();
        if (waveCtxRef.current?.state === "suspended") waveCtxRef.current.resume();
      } catch {}
      startGreetingRef.current?.();
      // Tarayıcı bir konuşmayı engellediyse (kullanıcı etkileşimi yoktu) — şimdi oynat
      const pending = pendingSayRef.current;
      if (pending) { pendingSayRef.current = null; sayText(pending); }
    };
    window.addEventListener("pointerdown", h);
    window.addEventListener("keydown", h);
    return () => { window.removeEventListener("pointerdown", h); window.removeEventListener("keydown", h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3) Tudi'ye (veya sohbete) dokunur dokunmaz KONUŞSUN — kullanıcı kararı 2026-09-26 ("direkt açtığımızda direkt konuşsun")
  const openGreetedRef = useRef(false);
  useEffect(() => {
    const opened = open || nanoOpen || liveOpen;
    if (!opened || openGreetedRef.current || mutedRef.current) return;
    openGreetedRef.current = true;
    if (audioRef.current && !audioRef.current.paused) return; // site selamı zaten çalıyor — üstüne binme
    stopAudio();
    const text = stripTokens(greeting || `${t("video_cap1a")}! ${t("chat_sub")}`);
    const id = setTimeout(() => sayText(text), 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nanoOpen, liveOpen]);

  // (Tudi Live sahne yönetimi tüm state'lerin ardından, return'den hemen önce hesaplanır — aşağıda)

  // Sohbet paneli kendiliğinden AÇILMAZ — Tudi yüzen sesli küre olarak çalışır (kullanıcı isterse butondan açar)
  const userToggledRef = useRef(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-tudi-chat", handler);
    const nanoHandler = () => setNanoOpen(true);
    window.addEventListener("tudi-nano-open", nanoHandler);
    return () => {
      window.removeEventListener("open-tudi-chat", handler);
      window.removeEventListener("tudi-nano-open", nanoHandler);
    };
  }, []);

  useEffect(() => {
    if (open && !greetedRef.current) {
      greetedRef.current = true;
      setMessages([{ role: "assistant", content: t("chat_welcome") }]);
    }
  }, [open, t]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (presetText = null, fromVoice = false) => {
    const text = (presetText ?? input).trim();
    if (!text || busy) return;
    const userTopic = detectLiveTopic(text);
    if (userTopic) {
      // Sahne değişiyorsa ve canlı sahne açıksa (ya da sesli konuşma): Tudi kısa sunum cümlesini SESLİ söyler
      const changed = liveTopicRef.current?.key !== userTopic;
      setLiveTopic({ key: userTopic, v: Math.random() < 0.5 ? "-b" : "" }); // konuşulan kanser → canlı sahne o organa döner (varsa ikinci açıdan)
      if (changed && (liveOpenRef.current || fromVoice) && !mutedRef.current) {
        sayText(LIVE_PRESENT_LINES[userTopic] || "Görüntüyü ekrana alıyorum.");
      }
    }
    stopAudio(); // onceki konusma aninda susturulur — sesler ust uste binmez
    if (fromVoice) setLiveOpen(true); // sesli konuşan ziyaretçi → tam ekran sohbet sahnesi kendiliğinden açılır
    setInput("");
    ttsQueueRef.current = [];
    sentenceBufRef.current = "";
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    let spokenText = ""; // yazılı sohbette: uzun cevabın kısa SESLİ özeti (akıcı konuşma — yazı detaylı kalır)
    let fullAnswer = ""; // cevap metni — konu tespiti (sahne) için
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionRef.current, message: text, voice: fromVoice, lang, speak: !mutedRef.current && !fromVoice }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") break;
          try {
            const obj = JSON.parse(payload);
            if (obj.speech) spokenText = obj.speech;
            if (obj.delta) {
              fullAnswer += obj.delta;
              if (fromVoice) enqueueSpeech(obj.delta); // yalnızca mikrofonla sorulanlar akarken okunur (o cevap zaten kısa)
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t("chat_error") };
        return copy;
      });
      // Sesli konuşan ziyaretçi boşluğa bakmasın — Tudi nazikçe sesli söylesin
      if ((fromVoice || liveOpenRef.current) && !mutedRef.current) {
        sayText("Üzgünüm, şu an kısa bir bağlantı sorunu yaşıyorum; birkaç saniye sonra tekrar dener misiniz?");
      }
    } finally {
      setBusy(false);
      if (fromVoice) flushSpeech();
      if (spokenText && !mutedRef.current && !fromVoice) sayText(spokenText);
      const answerTopic = detectLiveTopic(fullAnswer); // Tudi'nin anlattığı konu da sahneyi çevirir
      if (answerTopic) setLiveTopic({ key: answerTopic, v: Math.random() < 0.5 ? "-b" : "" });
      // NOT: Sayfa artık kendiliğinden başka sayfaya KAYMAZ (kullanıcı kararı 2026-09-26: "site kayıyor").
      // İlgili sayfa, mesajın altındaki butonla kullanıcının tıklamasına bırakılır.
    }
  };

  // --- butonsuz sesli sohbet (mikrofon) ---
  const [recording, setRecording] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const voiceModeRef = useRef(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  voiceModeRef.current = voiceMode;

  const toggleMic = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) return;
        try {
          const fd = new FormData();
          fd.append("file", blob, "konusma.webm");
          const r = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = r.data.text;
          if (text && !isNoiseText(text) && !isEchoOf(text, lastTtsTextRef.current)) { ackThen(async () => { if (await waitForFree()) send(text, true); }); }
        } catch { /* sessiz geç */ }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      setVoiceMode(true);
    } catch { /* mikrofon izni yok */ }
  };

  // Sesli konuşmada SIFIR gecikme hissi: cevap üretilirken Tudi anında kısa karşılama söyler (önceden önbelleğe alınmış sesler — sunucuya bile gidilmez)
  const ackAudiosRef = useRef([]);
  useEffect(() => {
    const phrases = ["Anlıyorum, hemen bakıyorum.", "Tabii, bir saniye.", "Hemen ilgileniyorum."];
    Promise.all(phrases.map((p) => api.post("/tts", { text: p, lang }).then((r) => absoluteUrl(r.data.url)).catch(() => null)))
      .then((urls) => { ackAudiosRef.current = urls.filter(Boolean).map((u) => new Audio(u)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);
  const ackThen = (fn) => {
    const items = ackAudiosRef.current;
    if (!items.length || mutedRef.current) { fn(); return; }
    const a = items[Math.floor(Math.random() * items.length)];
    try { a.currentTime = 0; } catch {}
    a.onended = fn;
    a.onerror = fn;
    a.play().then(() => setOrbLine("Anlıyorum…")).catch(fn);
  };

  // --- eller serbest asistan: dinle → anla → konuş → sayfayı aç → tekrar dinle ---
  const [handsFree, setHandsFree] = useState(false);
  const [micDenied, setMicDenied] = useState(false);
  const [pttActive, setPttActive] = useState(false);
  const pttRecRef = useRef(null);

  // Mikrofon izni YALNIZCA canlı sahneye girerken istenir — siteyi inceleyen ziyaretçi rahatsız edilmez (kullanıcı kararı 2026-10-05)
  const wokeRef = useRef(false);
  useEffect(() => {
    if (!liveOpen || handsFree || micDenied || wokeRef.current) return;
    wokeRef.current = true;
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
        s.getTracks().forEach((tr) => tr.stop());
        setHandsFree(true);
        setVoiceMode(true);
      }).catch(() => setMicDenied(true));
    }
  }, [liveOpen, handsFree, micDenied]);

  // BASILI TUT & KONUŞ (güvenilir yedek yol — VAD'sız, telefonda her zaman çalışır)
  const pttStart = async () => {
    if (pttRecRef.current || busy) return;
    try {
      if (handsFreeRef.current) { setHandsFree(false); await new Promise((r) => setTimeout(r, 300)); } // otomatik dinlemeyle çakışmasın
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setPttActive(false);
        pttRecRef.current = null;
        window.__tudiMicLevel = 0;
        const mime = rec.mimeType || "audio/webm";
        const blob = new Blob(chunks, { type: mime });
        if (blob.size < 800) return;
        try {
          setOrbLine("Anlıyorum…");
          const fd = new FormData();
          fd.append("file", blob, mime.includes("mp4") ? "ptt.mp4" : "ptt.webm");
          const r = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (r.data.text && !isNoiseText(r.data.text) && !isEchoOf(r.data.text, lastTtsTextRef.current)) { setOrbLine(r.data.text); ackThen(async () => { if (await waitForFree()) send(r.data.text, true); }); }
          else setOrbLine("Sizi duyamadım — biraz daha yakından dener misiniz?");
        } catch { setOrbLine(""); }
      };
      // seviye çubuğu için analiz
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const iv = setInterval(() => {
        if (!pttRecRef.current) { clearInterval(iv); ctx.close().catch(() => {}); window.__tudiMicLevel = 0; return; }
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += Math.abs(v - 128);
        window.__tudiMicLevel = Math.min(1, sum / buf.length / 6);
      }, 100);
      rec.start();
      pttRecRef.current = rec;
      setPttActive(true);
      setMicDenied(false);
    } catch { setMicDenied(true); }
  };
  const pttStop = () => {
    const rec = pttRecRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  };

  const handsFreeRef = useRef(false);
  const listenActiveRef = useRef(false);
  const listenRecRef = useRef(null); // süren eller-serbest kayıt — Tudi konuşunca iptal edilir
  const discardListenRef = useRef(false);
  const cancelListeningRef = useRef(null);
  const busyRef = useRef(false);
  const speakingRef = useRef(false);
  const scheduleListenRef = useRef(null);
  handsFreeRef.current = handsFree;
  busyRef.current = busy;
  speakingRef.current = speakingIdx !== null || greetSpeaking || orbSpeaking || ttsPlayingRef.current;

  // Donusum durtusu kaldirildi (kullanici karari: ekranda yalnizca Tudi kuresi + WhatsApp)

  cancelListeningRef.current = () => {
    // Tudi konuşmaya başlarken süren kaydı durdur ve İPTAL olarak işaretle — içinde Tudi'nin kendi sesi olabilir
    if (listenActiveRef.current && listenRecRef.current && listenRecRef.current.state !== "inactive") {
      discardListenRef.current = true;
      try { listenRecRef.current.stop(); } catch {}
    }
  };

  const startListening = async () => {
    if (listenActiveRef.current || !handsFreeRef.current) return;
    listenActiveRef.current = true;
    setRecording(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const t0 = Date.now();
      let lastSound = Date.now();
      let spoke = false;
      let floor = null; // uyarlanabilir gürültü tabanı — her telefonun mikrofon hassasiyeti farklı
      let floorSum = 0, floorN = 0;
      const iv = setInterval(() => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += Math.abs(v - 128);
        const avg = sum / buf.length;
        const elapsed0 = Date.now() - t0;
        window.__tudiMicLevel = Math.min(1, avg / 6); // canlı sahnedeki seviye çubuğu
        if (elapsed0 <= 600) { floorSum += avg; floorN++; }
        else if (floor === null) floor = Math.max(0.7, floorN ? floorSum / floorN : 1);
        const threshold = floor !== null ? floor * 1.8 + 0.4 : 999; // konuşma = ortam gürültüsünün ~2 katı
        if (floor !== null && avg > threshold) { lastSound = Date.now(); spoke = true; }
        const silentFor = Date.now() - lastSound;
        const elapsed = Date.now() - t0;
        if (!handsFreeRef.current || (spoke && silentFor > 1000) || elapsed > 14000) {
          clearInterval(iv);
          rec.stop();
        }
      }, 120);
      rec.onstop = async () => {
        clearInterval(iv);
        stream.getTracks().forEach((tr) => tr.stop());
        ctx.close().catch(() => {});
        listenActiveRef.current = false;
        listenRecRef.current = null;
        setRecording(false);
        const discard = discardListenRef.current;
        discardListenRef.current = false;
        if (discard) return; // Tudi konuşmak için kaydı kesti — bu kayıtta Tudi'nin kendi sesi olabilir, STT'ye gönderilmez
        const mime = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mime });
        if (spoke && blob.size > 1000 && handsFreeRef.current) {
          try {
            const fd = new FormData();
            fd.append("file", blob, mime.includes("mp4") ? "konusma.mp4" : "konusma.webm"); // iOS mp4 üretir
            const r = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const heard = r.data.text;
            if (heard && !isNoiseText(heard) && !isEchoOf(heard, lastTtsTextRef.current) && handsFreeRef.current) { setOrbLine(heard); ackThen(async () => { if (await waitForFree()) send(heard, true); }); return; }
          } catch { /* sessiz geç */ }
        }
        scheduleListenRef.current?.();
      };
      rec.start();
      listenRecRef.current = rec;
    } catch {
      listenActiveRef.current = false;
      setRecording(false);
      setHandsFree(false);
      setMicDenied(true); // canlı sahnede "izin gerekli" göstergesi
    }
  };

  const scheduleListen = () => {
    if (!handsFreeRef.current || listenActiveRef.current) return;
    setTimeout(() => {
      if (handsFreeRef.current && !listenActiveRef.current && !busyRef.current && !speakingRef.current) startListening();
    }, 700);
  };
  scheduleListenRef.current = scheduleListen;

  useEffect(() => {
    if (handsFree && !busy && speakingIdx === null && !greetSpeaking && !orbSpeaking && !recording) scheduleListen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handsFree, open, busy, speakingIdx, greetSpeaking, orbSpeaking, recording]);

  // Tudi Live sahne yönetimi: video ile açılır; konuşulan kanser organına göre sahne değişir,
  // konuşurken "konusma", dinlerken "dinleme", sohbet boşluğunda "genel" sahnesi görünür
  const conversed = messages.length > 1;
  const isSpeakingNow = orbSpeaking || greetSpeaking || speakingIdx !== null;
  const liveSceneKey = liveTopic?.key || (isSpeakingNow ? "konusma" : recording ? "dinleme" : conversed ? "genel" : "video");
  const liveSceneSrc = `/img/live/${liveSceneKey}${liveTopic && LIVE_VARIANTS.has(liveTopic.key) ? liveTopic.v : ""}.jpg`;
  useEffect(() => {
    const v = liveVideoRef.current;
    if (!v) return;
    if (liveOpen && liveSceneKey === "video") { v.muted = true; v.play().catch(() => {}); } else { v.pause(); }
  }, [liveOpen, liveSceneKey]);


  return (
    <>
      {/* Nano-panel tetikleyici kaldırıldı (master yönerge: tek yardımcı katman) — panel,
          kapı turunda / sesli yanıtta / dergi okumada kendiliğinden açılır */}
      {/* Eller serbest küre: sohbet penceresi açmadan konuşan asistan — dokununca piksel panel açılır, konuşma kesilmez */}
      {handsFree && !open && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[80] flex flex-col items-end gap-2" data-testid="tudi-orb">
          <button onClick={() => { userToggledRef.current = true; setLiveOpen((v) => !v); }} className="relative w-16 h-16" data-testid="tudi-orb-button" aria-label="AI Assistant" title={t("nano_open")}>
            <span className={`absolute inset-0 rounded-full ${recording ? "bg-red-400/40 animate-ping" : "bg-emerald-400/20 animate-pulse"}`} />
            <span ref={orbRingRef} data-testid="tudi-wave-ring" className={`absolute inset-0 rounded-full border-2 border-brand pointer-events-none ${orbSpeaking || greetSpeaking ? "tudi-wave-live" : ""}`} style={{ opacity: 0 }} />
            <span ref={orbRing2Ref} data-testid="tudi-wave-ring-2" className={`absolute inset-0 rounded-full border border-brand/70 pointer-events-none ${orbSpeaking || greetSpeaking ? "tudi-wave-live-2" : ""}`} style={{ opacity: 0 }} />
            <span className="relative block w-16 h-16 rounded-full bg-white shadow-2xl shadow-brand/40 border-2 border-white overflow-hidden">
              <img src="/tudi-head.png" alt="Tudi" className="w-full h-full object-contain p-1" />
            </span>
            <span className={`absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${recording ? "bg-red-500" : busy ? "bg-amber-400" : "bg-emerald-400"}`} />
          </button>
        </motion.div>
      )}
      {(!handsFree || open) && (
      <motion.button
        data-testid="chat-open-button"
        onClick={() => { userToggledRef.current = true; if (open) { setOpen(false); } else { setLiveOpen(true); } }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 200 }}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[80] w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-2xl overflow-hidden border-2 border-white bg-ink hover:scale-105 transition-transform duration-300"
        aria-label="AI Assistant"
        title={t("nano_open")}
      >
        <img src="/tudi-head.png" alt="Tudi" className="w-full h-full object-contain p-1 bg-white" />
        <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />
      </motion.button>
      )}
      {!open && !handsFree && <span data-testid="tudi-alive-ring" className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[79] w-14 h-14 sm:w-16 sm:h-16 rounded-full border-2 border-brand/50 animate-ping pointer-events-none" />}

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-window"
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[90] w-[min(92vw,380px)] h-[520px] rounded-3xl overflow-hidden flex flex-col shadow-2xl border border-black/10 glass"
            style={{ background: "rgba(255,255,255,0.92)" }}
          >
            <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-[#3A0303] via-brand to-[#5A1220] text-white">
              <div className="relative shrink-0">
                <img src="/tudi-head.png" alt="" className="w-14 h-14 rounded-full object-contain bg-[#F7F1E3] ring-2 ring-[#D4AF37]/70 shadow-[0_0_18px_rgba(212,175,55,0.45)]" />
                <span className={`absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full border-2 border-brand ${recording ? "bg-emerald-400 animate-pulse" : "bg-[#D4AF37]"}`} />
              </div>
              <div className="flex-1 leading-tight">
                <div className="font-heading font-bold text-[15px] tracking-wide">{t("chat_title")}</div>
                <div className="text-[11px] text-white/70 flex items-center gap-1.5">
                  {greetSpeaking && (
                    <span className="flex items-end gap-0.5 h-3 shrink-0" data-testid="tudi-header-bars">
                      <span className="w-0.5 bg-[#E9CF8A] rounded animate-pulse" style={{ height: "60%" }} />
                      <span className="w-0.5 bg-[#E9CF8A] rounded animate-pulse [animation-delay:150ms]" style={{ height: "100%" }} />
                      <span className="w-0.5 bg-[#E9CF8A] rounded animate-pulse [animation-delay:300ms]" style={{ height: "40%" }} />
                    </span>
                  )}
                  {recording ? "Sizi dinliyorum…" : t("chat_sub")}
                </div>
              </div>
              <button data-testid="chat-mute-toggle" onClick={toggleMute} title={muted ? "Sesi aç" : "Sessize al"} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center">
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => {
                const hasPanel = m.role === "assistant" && hasPanelToken(m.content);
                const hasDoc = m.role === "assistant" && hasDoctorToken(m.content);
                const navRoute = m.role === "assistant" && m.content ? getNavRoute(m.content) : null;
                const appData = m.role === "assistant" && m.content ? getApplicationData(m.content) : null;
                const shown = hasPanel || hasDoc || navRoute || appData ? stripTokens(m.content) : m.content;
                return (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-brand text-white rounded-br-md" : "bg-black/5 text-ink rounded-bl-md"}`}>
                    {shown
                      ? shown.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
                          part.startsWith("**") && part.endsWith("**") ? <strong key={j}>{part.slice(2, -2)}</strong> : part
                        )
                      : busy && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin" /> : ""}
                    {hasPanel && !busy && (
                      <a
                        data-testid={`panel-cta-${i}`}
                        href="/panel"
                        className="mt-2.5 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-brand text-white text-[11px] font-bold hover:bg-branddeep transition-colors"
                      >
                        <UploadCloud size={13} /> {t("chat_go_panel")}
                      </a>
                    )}
                    {hasDoc && !busy && <DoctorCard />}
                    {appData && !busy && <ApplicationCard data={appData} />}
                    {navRoute && !busy && (
                      <a
                        data-testid={`nav-cta-${i}`}
                        href={navRoute}
                        className="mt-2.5 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-ink text-white text-[11px] font-bold hover:bg-black transition-colors"
                      >
                        {t("chat_nav_open")}
                      </a>
                    )}
                    {m.role === "assistant" && m.content && (
                      <button
                        data-testid={`tts-button-${i}`}
                        onClick={() => speak(i, shown)}
                        className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-brand/70 hover:text-brand transition-colors"
                        title="Sesli dinle"
                      >
                        {ttsLoading === i ? <Loader2 size={12} className="animate-spin" /> : speakingIdx === i ? <Square size={12} /> : <Volume2 size={12} />}
                        {speakingIdx === i ? "…" : ""}
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
              {messages.length <= 1 && !busy && (
                <div className="flex flex-col items-center mt-1" data-testid="chat-welcome-hero">
                  <motion.img
                    src="/tudi-body.png"
                    alt="Tudi"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="h-32 object-contain drop-shadow-[0_14px_30px_rgba(74,4,4,0.25)]"
                  />
                  <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent my-3" />
                </div>
              )}
              {messages.length <= 1 && !busy && (
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="chat-chips">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      data-testid={`chat-chip-${n}`}
                      onClick={() => send(t(`chat_chip${n}`))}
                      className="px-3 py-1.5 rounded-full bg-brand/10 text-brand text-[11px] font-semibold hover:bg-brand/20 transition-colors text-left"
                    >
                      {t(`chat_chip${n}`)}
                    </button>
                  ))}
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="p-3 border-t border-black/5">
              {handsFree && (
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold text-brand" data-testid="handsfree-indicator">
                  <span className={`w-1.5 h-1.5 rounded-full ${recording ? "bg-red-500 animate-pulse" : "bg-emerald-500"}`} />
                  {t("chat_handsfree")}
                </div>
              )}
              <div className="flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={recording ? t("chat_listening") : t("chat_placeholder")}
                className="flex-1 h-11 px-4 rounded-full bg-black/5 text-sm text-ink placeholder:text-sub/70 focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
              <button
                data-testid="chat-mic-button"
                onClick={handsFree ? () => setHandsFree(false) : toggleMic}
                title={handsFree ? t("chat_handsfree_off") : t("chat_voice_mode")}
                className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${handsFree ? "bg-emerald-500 text-white" : recording ? "bg-red-500 text-white animate-pulse" : voiceMode ? "bg-brand/15 text-brand" : "bg-black/5 text-sub hover:text-brand"}`}
              >
                <Mic size={16} />
              </button>
              <button data-testid="chat-send-button" onClick={() => send()} disabled={busy} className="w-11 h-11 rounded-full bg-brand text-white flex items-center justify-center hover:bg-branddeep disabled:opacity-50 transition-colors">
                <SendHorizonal size={16} />
              </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <NanoPanel
        open={nanoOpen}
        onClose={() => setNanoOpen(false)}
        line={orbLine}
        speaking={orbSpeaking || greetSpeaking || speakingIdx !== null}
        onOpenChat={() => setOpen(true)}
      />
      {/* TUDI LIVE — video ile açılır; konuşulan organın sahnesi yumuşakça belirir; ortada küre YOK, büyük Tudi */}
      <AnimatePresence>
        {liveOpen && (
          <motion.div
            key="tudi-live"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[300] overflow-hidden"
            style={{ background: "#050102" }}
            data-testid="tudi-live-overlay"
          >
            {/* Açılış videosu — Japon zarafeti (kullanıcının yüklediği sahne) */}
            <video
              ref={liveVideoRef}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              autoPlay
              loop
              playsInline
              preload="auto"
              data-testid="tudi-live-bg-video"
            >
              <source src="/video/tudi-live-bg.webm" type="video/webm" />
              <source src="/video/tudi-live-bg.mp4" type="video/mp4" />
            </video>
            {/* Konu/durum sahnesi — videonun üstünde yumuşak geçişle belirir */}
            <AnimatePresence>
              {liveSceneKey !== "video" && (
                <motion.img
                  key={liveSceneSrc}
                  src={liveSceneSrc}
                  alt=""
                  data-testid="tudi-live-scene"
                  className="absolute inset-0 w-full h-full object-cover object-[26%_center] sm:object-center pointer-events-none"
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                />
              )}
            </AnimatePresence>
            {/* Okunabilirlik örtüsü — üst ve alt karartı */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(5,1,2,0.55) 0%, rgba(5,1,2,0.05) 30%, rgba(5,1,2,0.12) 55%, rgba(5,1,2,0.88) 100%)" }} />
            {/* Üst bar */}
            <div className="absolute top-0 inset-x-0 flex flex-col items-center pt-6 gap-1.5 pointer-events-none">
              <div className="text-[10px] font-heading font-semibold tracking-[0.45em] uppercase text-[#E9CF8A]" style={{ textShadow: "0 0 16px rgba(212,175,55,0.5)" }}>Tudi · Canlı Sohbet</div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#E9CF8A]/80 font-heading text-center px-10 leading-relaxed" style={{ textShadow: "0 0 12px rgba(212,175,55,0.4)" }} data-testid="tudi-live-status">
                {isSpeakingNow ? "Tudi konuşuyor" : pttActive ? "Dinliyorum — bırakınca cevaplarım" : recording ? "Sizi dinliyorum" : micDenied ? "Mikrofon izni gerekli" : ""}
              </div>
              {/* Mikrofon seviye çubuğu — sesinizin duyulduğunun görsel kanıtı */}
              {(recording || pttActive) && (
                <div className="h-1 w-44 bg-white/15 rounded-full overflow-hidden" data-testid="tudi-live-micbar">
                  <div ref={micBarRef} className="h-full bg-gradient-to-r from-[#8A2434] to-[#D4AF37] transition-[width] duration-100" style={{ width: "0%" }} />
                </div>
              )}
            </div>
            <button
              onClick={() => setLiveOpen(false)}
              data-testid="tudi-live-close"
              className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full bg-black/40 backdrop-blur border border-[#D4AF37]/30 text-[#D4AF37]/80 hover:text-[#D4AF37] hover:border-[#D4AF37]/60 flex items-center justify-center transition-colors"
              aria-label="Kapat"
            >
              <X size={17} />
            </button>
            {/* Büyük Tudi — yalnızca açılış videosunda önde; organ sahnelerinde sahnedeki Tudi sunumu devralır */}
            <AnimatePresence>
              {liveSceneKey === "video" && (
                <motion.img
                  src="/tudi-body.png"
                  alt="Tudi"
                  data-testid="tudi-live-body"
                  className="absolute bottom-0 left-1 sm:left-10 h-[32vh] sm:h-[56vh] max-w-[36vw] object-contain pointer-events-none"
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0, y: [0, -9, 0] }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ opacity: { duration: 0.9 }, x: { duration: 0.9 }, y: { duration: 6, repeat: Infinity, ease: "easeInOut" } }}
                  style={{ filter: "drop-shadow(0 18px 50px rgba(212,175,55,0.35))" }}
                />
              )}
            </AnimatePresence>
            {/* Alt diyalog — karşılıklı sohbet ekranda akar */}
            <div className="absolute bottom-0 inset-x-0 flex flex-col items-center px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="w-full max-w-2xl space-y-2.5 max-h-[34vh] sm:max-h-[38vh] overflow-hidden" data-testid="tudi-live-dialog">
                {messages.slice(-3, -1).map((m, i) => (
                  <p key={i} className={m.role === "user"
                    ? "text-right text-[13px] text-[#E9CF8A]/85 font-heading italic"
                    : "text-left text-[14px] text-[#F2E9D5]/90 font-heading italic leading-relaxed"}>
                    {m.role === "user" ? "Siz: " : "Tudi: "}{stripTokens(m.content).slice(0, 180)}
                  </p>
                ))}
                <p className="text-left text-[15px] text-[#FCF6E6] font-heading italic leading-relaxed" style={{ textShadow: "0 1px 14px rgba(0,0,0,0.8), 0 0 20px rgba(212,175,55,0.3)" }} data-testid="tudi-live-line">
                  {orbLine || stripTokens(messages[messages.length - 1]?.content || "").slice(0, 180)}
                </p>
              </div>
              <div className="flex items-center gap-3 mt-5 flex-wrap justify-center">
                {/* Basılı tut & konuş — otomatik dinleme zorlanırsa her zaman çalışan güvenilir yol */}
                <button
                  onPointerDown={pttStart}
                  onPointerUp={pttStop}
                  onPointerLeave={pttStop}
                  onPointerCancel={pttStop}
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid="tudi-live-ptt"
                  className={`h-12 px-6 rounded-full text-[13px] font-heading font-bold tracking-wider border backdrop-blur transition-all select-none touch-none ${pttActive ? "bg-[#D4AF37] text-[#1A0608] border-[#D4AF37] scale-95" : "bg-gradient-to-r from-[#4A0404]/90 to-[#6B0F12]/90 text-[#F5E7C6] border-[#D4AF37]/50 hover:border-[#D4AF37]"}`}
                >
                  {pttActive ? "Dinliyorum…" : "Basılı tut & konuş"}
                </button>
                <button
                  onClick={() => setHandsFree((v) => !v)}
                  data-testid="tudi-live-mic-toggle"
                  className={`h-11 px-5 rounded-full text-[12px] font-heading font-bold tracking-wider border backdrop-blur transition-all ${handsFree ? "bg-[#4A0404]/85 text-[#F5E7C6] border-[#D4AF37]/50" : "bg-black/35 text-[#E9CF8A]/80 border-[#D4AF37]/30 hover:border-[#D4AF37]/60"}`}
                >
                  {micDenied ? "Mikrofona izin ver" : handsFree ? "Otomatik dinleme açık" : "Otomatik dinlemeyi aç"}
                </button>
                <button
                  onClick={() => { setLiveOpen(false); setOpen(true); }}
                  data-testid="tudi-live-open-chat"
                  className="h-11 px-5 rounded-full text-[12px] font-heading font-bold tracking-wider bg-black/35 backdrop-blur text-[#E9CF8A]/80 border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 transition-all"
                >
                  Yazılı sohbet
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
